import { Alert, Group, Text, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconUserCheck, IconUserX, IconCopy, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';

export const ImpersonationBanner = () => {
  const { user: appUser } = useAppUser();

  const handleStopImpersonation = async () => {
    if (!appUser) return;
    
    try {
      const token = await appUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/stop-impersonation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to stop impersonation',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to stop impersonation',
        color: 'red',
      });
    }
  };

  const handleCopyUserId = () => {
    if (!appUser?.uid) return;
    navigator.clipboard.writeText(appUser.uid);
    notifications.show({
      message: 'User ID copied',
      color: 'green',
      icon: <IconCheck size={14} />,
      autoClose: 2000,
    });
  };

  if (!appUser?._adminOverride) {
    return null;
  }

  return (
    <Alert
      icon={<IconUserCheck size="1rem" />}
      color="orange"
      py={6}
      // The Alert root clips overflow and its body is flex:1 with no
      // min-width:0, so without these the row can't shrink and anything past
      // the edge gets cut off instead of wrapping.
      styles={{ body: { minWidth: 0 }, message: { minWidth: 0 } }}
      style={{
        margin: 0,
        borderRadius: 0,
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: '2px solid var(--mantine-color-orange-6)',
        backgroundColor: 'var(--mantine-color-orange-1)',
      }}
    >
      {/* One row on wide screens, since the banner is persistent and every
          line it takes is a line every page loses. Wraps rather than clips
          when it runs out of room, so the button is always reachable. */}
      <Group justify="space-between" align="center" gap="xs">
        <Group gap={6} align="center" style={{ minWidth: 0 }}>
          <Text size="sm">
            Impersonating <strong>{appUser.displayName || appUser.email}</strong>
          </Text>
          <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
            · {appUser.uid}
          </Text>
          <Tooltip label="Copy user ID">
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              style={{ flexShrink: 0 }}
              onClick={handleCopyUserId}
            >
              <IconCopy size="0.7rem" />
            </ActionIcon>
          </Tooltip>
          <Text size="xs" c="dimmed" visibleFrom="sm">
            · Admin: {appUser._adminOverride.originalAdminEmail}
          </Text>
        </Group>
        <Button
          leftSection={<IconUserX size="0.9rem" />}
          size="xs"
          variant="outline"
          color="orange"
          style={{ flexShrink: 0 }}
          onClick={handleStopImpersonation}
        >
          Stop Impersonation
        </Button>
      </Group>
    </Alert>
  );
};